import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LogOut, Mail, Phone, Calendar, Inbox, CheckCircle,
  Trash2, Eye, RefreshCw, Search, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, read: 0, responded: 0 });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const adminEmail = localStorage.getItem('rra_admin_email');
  const token = localStorage.getItem('rra_admin_token');

  const authHeaders = { Authorization: `Bearer ${token}` };

  const handleLogout = useCallback(() => {
    localStorage.removeItem('rra_admin_token');
    localStorage.removeItem('rra_admin_email');
    navigate('/admin/login');
  }, [navigate]);

  const fetchData = useCallback(async () => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    setLoading(true);
    try {
      const [inquiriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/inquiries`, { headers: authHeaders }),
        axios.get(`${API}/admin/inquiries/stats`, { headers: authHeaders }),
      ]);
      setInquiries(inquiriesRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        handleLogout();
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load inquiries.',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate, handleLogout, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/inquiries/${id}/status`, { status }, { headers: authHeaders });
      toast({ title: 'Status updated', description: `Marked as ${status}.` });
      fetchData();
      if (selectedInquiry?.id === id) {
        setSelectedInquiry({ ...selectedInquiry, status });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update status.', variant: 'destructive' });
    }
  };

  const deleteInquiry = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${deleteTarget.id}`, { headers: authHeaders });
      toast({ title: 'Deleted', description: 'Inquiry removed successfully.' });
      setDeleteTarget(null);
      setSelectedInquiry(null);
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete inquiry.', variant: 'destructive' });
    }
  };

  const openInquiry = async (inquiry) => {
    setSelectedInquiry(inquiry);
    if (inquiry.status === 'new') {
      await updateStatus(inquiry.id, 'read');
    }
  };

  const filteredInquiries = inquiries.filter((inq) => {
    const matchesStatus = filterStatus === 'all' || inq.status === filterStatus;
    const search = searchTerm.toLowerCase();
    const matchesSearch =
      !search ||
      inq.name.toLowerCase().includes(search) ||
      inq.email.toLowerCase().includes(search) ||
      inq.message.toLowerCase().includes(search);
    return matchesStatus && matchesSearch;
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-MY', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statusColors = {
    new: 'bg-blue-100 text-blue-700 border-blue-200',
    read: 'bg-amber-100 text-amber-700 border-amber-200',
    responded: 'bg-green-100 text-green-700 border-green-200',
  };

  const inquiryTypeLabels = {
    general: 'General Inquiry',
    programs: 'Programme Information',
    workshops: 'Parent Workshops',
    training: 'Teacher Training',
    enrollment: 'Enrollment',
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_early-literacy-hub-3/artifacts/yw9o3e9y_image.png"
              alt="Read Right Academy"
              className="h-10"
            />
            <div>
              <h1 className="text-lg font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-xs text-gray-500">{adminEmail}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={fetchData}
              data-testid="refresh-btn"
              className="hidden sm:flex"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open('/', '_blank')}
              className="hidden sm:flex"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View Site
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="p-5" data-testid="stat-total">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600 font-medium">Total Inquiries</p>
              <Inbox className="h-5 w-5 text-gray-400" />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
          </Card>
          <Card className="p-5 border-blue-200" data-testid="stat-new">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-blue-700 font-medium">New</p>
              <Mail className="h-5 w-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-blue-700">{stats.new}</p>
          </Card>
          <Card className="p-5 border-amber-200" data-testid="stat-read">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-amber-700 font-medium">Read</p>
              <Eye className="h-5 w-5 text-amber-500" />
            </div>
            <p className="text-3xl font-bold text-amber-700">{stats.read}</p>
          </Card>
          <Card className="p-5 border-green-200" data-testid="stat-responded">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-green-700 font-medium">Responded</p>
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-green-700">{stats.responded}</p>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, or message..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-input"
                className="pl-10 border-2"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {['all', 'new', 'read', 'responded'].map((s) => (
                <Button
                  key={s}
                  variant={filterStatus === s ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus(s)}
                  data-testid={`filter-${s}`}
                  className={filterStatus === s ? 'bg-[#3B4BA0] hover:bg-[#2d3a7a]' : ''}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                  {s !== 'all' && ` (${stats[s] || 0})`}
                </Button>
              ))}
            </div>
          </div>
        </Card>

        {/* Inquiries List */}
        {loading ? (
          <div className="text-center py-20 text-gray-500">
            <RefreshCw className="h-8 w-8 mx-auto mb-3 animate-spin" />
            Loading inquiries...
          </div>
        ) : filteredInquiries.length === 0 ? (
          <Card className="p-12 text-center">
            <Inbox className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No inquiries found</h3>
            <p className="text-sm text-gray-500">
              {inquiries.length === 0
                ? 'Inquiries from your contact form will appear here.'
                : 'Try changing the filter or search term.'}
            </p>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="inquiries-list">
            {filteredInquiries.map((inq) => (
              <Card
                key={inq.id}
                data-testid={`inquiry-${inq.id}`}
                onClick={() => openInquiry(inq)}
                className={`p-5 cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4 ${
                  inq.status === 'new'
                    ? 'border-l-blue-500'
                    : inq.status === 'read'
                    ? 'border-l-amber-500'
                    : 'border-l-green-500'
                }`}
              >
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900">{inq.name}</h3>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full border font-medium ${statusColors[inq.status]}`}
                      >
                        {inq.status.toUpperCase()}
                      </span>
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                        {inquiryTypeLabels[inq.inquiry_type] || inq.inquiry_type}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">{inq.message}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {inq.email}
                      </span>
                      {inq.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {inq.phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(inq.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Inquiry Detail Dialog */}
      <Dialog open={!!selectedInquiry} onOpenChange={(open) => !open && setSelectedInquiry(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedInquiry && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">{selectedInquiry.name}</DialogTitle>
                <DialogDescription className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full border font-medium ${statusColors[selectedInquiry.status]}`}
                  >
                    {selectedInquiry.status.toUpperCase()}
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                    {inquiryTypeLabels[selectedInquiry.inquiry_type] || selectedInquiry.inquiry_type}
                  </span>
                  <span className="text-xs text-gray-500">{formatDate(selectedInquiry.created_at)}</span>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Contact Details */}
                <div className="grid sm:grid-cols-2 gap-3">
                  <a
                    href={`mailto:${selectedInquiry.email}`}
                    className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 text-sm"
                  >
                    <Mail className="h-4 w-4 text-blue-600" />
                    <span className="text-blue-700 truncate">{selectedInquiry.email}</span>
                  </a>
                  {selectedInquiry.phone && (
                    <a
                      href={`https://wa.me/${selectedInquiry.phone.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-3 bg-green-50 rounded-lg hover:bg-green-100 text-sm"
                    >
                      <Phone className="h-4 w-4 text-green-600" />
                      <span className="text-green-700">{selectedInquiry.phone}</span>
                    </a>
                  )}
                </div>

                {/* Message */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Message</p>
                  <div className="bg-gray-50 rounded-lg p-4 text-gray-800 whitespace-pre-wrap">
                    {selectedInquiry.message}
                  </div>
                </div>
              </div>

              <DialogFooter className="flex-wrap gap-2 sm:gap-2">
                <Button
                  variant="outline"
                  onClick={() => updateStatus(selectedInquiry.id, 'new')}
                  disabled={selectedInquiry.status === 'new'}
                  data-testid="mark-new-btn"
                >
                  Mark as New
                </Button>
                <Button
                  variant="outline"
                  onClick={() => updateStatus(selectedInquiry.id, 'responded')}
                  disabled={selectedInquiry.status === 'responded'}
                  data-testid="mark-responded-btn"
                  className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark as Responded
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setDeleteTarget(selectedInquiry)}
                  data-testid="delete-btn"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete inquiry?</DialogTitle>
            <DialogDescription>
              This will permanently delete the inquiry from <strong>{deleteTarget?.name}</strong>.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={deleteInquiry}
              data-testid="confirm-delete-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Toaster />
    </div>
  );
};

export default AdminDashboard;
